import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { StudyInput } from "./StudyInput";
import { ContentUploadDialog } from "@/components/content/ContentUploadDialog";
import { PasteContentDialog } from "./PasteContentDialog";
import { Loader2, ChevronRight, Upload as UploadIcon, Link2, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { createChatFromStudyInput } from "@/lib/chat/createChatFromStudyInput";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCreateRecording } from "@/hooks/useCreateRecording";
import { toast } from "sonner";
import { checkUsageLimit, UsageLimitExceededError } from "@/hooks/useUsageLimits";
import { useUsageLimitContext } from "@/context/UsageLimitContext";
import { useTranslation } from "@/context/LanguageContext";
import { useContentProcessingToast } from "@/hooks/useContentProcessingToast";
import type { Plan } from "@/lib/ai/models";
import { RecordingTypeDialog } from "@/components/recording/RecordingTypeDialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

export const DashboardHeroStudy: React.FC<{ roomId?: string }> = ({ roomId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { createRecording, isCreating: isCreatingRecording } = useCreateRecording();
  const { showLimitError, setShowPricingDialog } = useUsageLimitContext();
  const { showProcessingToast } = useContentProcessingToast();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [recordingTypeOpen, setRecordingTypeOpen] = useState(false);
  const [isCheckingLimit, setIsCheckingLimit] = useState(false);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [contentReferenceIds, setContentReferenceIds] = useState<string[]>([]);
  const [userPlan, setUserPlan] = useState<Plan>("free");

  // Fetch subscription status
  useEffect(() => {
    if (!user) return;
    const fetchPlan = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status")
        .eq("id", user.id)
        .single();
      if (data?.subscription_status === "active") {
        setUserPlan("pro");
      }
    };
    fetchPlan();
  }, [user]);
  
  const handleUploadClick = async () => {
    setIsCheckingLimit(true);
    try {
      const check = await checkUsageLimit("content_uploads");
      if (!check.canProceed) {
        showLimitError(new UsageLimitExceededError("content_uploads", check.used, check.limit));
        return;
      }
      setUploadDialogOpen(true);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  const handlePasteClick = async () => {
    setIsCheckingLimit(true);
    try {
      const check = await checkUsageLimit("content_uploads");
      if (!check.canProceed) {
        showLimitError(new UsageLimitExceededError("content_uploads", check.used, check.limit));
        return;
      }
      setPasteDialogOpen(true);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  const handleRecordClick = async () => {
    setIsCheckingLimit(true);
    try {
      const check = await checkUsageLimit("content_uploads");
      if (!check.canProceed) {
        showLimitError(new UsageLimitExceededError("content_uploads", check.used, check.limit));
        return;
      }
      setRecordingTypeOpen(true);
    } finally {
      setIsCheckingLimit(false);
    }
  };

  const handleRecordingSourceSelect = (source: import("@/types/recording").RecordingSource) => {
    createRecording({ source, roomId });
  };

  const handleSubmit = async (
    text: string,
    options?: { studyModeTechniqueId?: string | null },
  ) => {
    if (!user) {
      toast.error("Please sign in to start a chat");
      return;
    }

    if (!text.trim()) return;

    try {
      const chat = await createChatFromStudyInput({
        userId: user.id,
        firstMessage: text,
        roomId: null,
        searchEnabled: isSearchEnabled,
        contentReferenceIds: contentReferenceIds.length > 0 ? contentReferenceIds : undefined,
        studyModeTechniqueId: options?.studyModeTechniqueId ?? null,
      });

      navigate(`/chat/${chat.id}`);
    } catch (error) {
      console.error("Error creating chat:", error);
      toast.error("Failed to start chat");
    }
  };

  const handleContentCreated = (contentId: string, contentType?: string) => {
    window.dispatchEvent(new CustomEvent("content-uploaded"));

    const type = contentType || "";
    const mediaTypes = ["audio", "video", "recording", "youtube"];
    const isMedia = mediaTypes.includes(type);

    // Pick phrase set based on content type.
    let phraseKeys: string[];
    if (type === "pdf") {
      phraseKeys = ["readingPages", "extractingText", "indexingChapters", "almostReady"];
    } else if (isMedia) {
      phraseKeys = ["uploading", "transcribing", "indexingChapters", "almostReady"];
    } else {
      phraseKeys = ["converting", "extractingText", "indexingChapters", "almostReady"];
    }

    const phrases = phraseKeys.map((k) => t(`dashboard.processing.phrases.${k}`));

    showProcessingToast({
      contentId,
      contentType: type,
      title: t("dashboard.processing.title"),
      phrases,
      readyLabel: t("dashboard.processing.ready"),
      onReady: () => navigate(`/content/${contentId}`),
    });
  };

  return (
    <div className="flex w-full flex-col items-center sm:min-h-[280px] min-h-[350px]">
      <div className="mb-5 flex flex-col items-center gap-2">
        <h2 className="text-xl text-center sm:text-3xl font-medium xl:text-4xl">
          {t("dashboard.whatToStudy")}
        </h2>
        <p className="text-base text-muted-foreground text-center">
          {t("dashboard.description")}
        </p>
      </div>

      <div className="z-10 w-full text-center md:max-w-lg xl:max-w-2xl flex flex-col">
        <TooltipProvider delayDuration={200}>
          <div className="grid w-full grid-cols-1 gap-2.5 sm:flex sm:items-center sm:justify-center sm:gap-3">
            <ActionCard
              onClick={handleUploadClick}
              loading={isCheckingLimit}
              title={t("dashboard.upload")}
              description={t("dashboard.uploadDesc")}
              tooltip={t("dashboard.uploadTooltip")}
              accent="emerald"
              Icon={UploadIcon}
            />
            <ActionCard
              onClick={handlePasteClick}
              loading={isCheckingLimit}
              title={t("dashboard.paste")}
              description={t("dashboard.pasteDesc")}
              tooltip={t("dashboard.pasteTooltip")}
              accent="blue"
              Icon={Link2}
            />
            <ActionCard
              onClick={handleRecordClick}
              loading={isCreatingRecording || isCheckingLimit}
              title={t("dashboard.record")}
              description={t("dashboard.recordDesc")}
              tooltip={t("dashboard.recordTooltip")}
              accent="rose"
              Icon={Mic}
            />
          </div>
        </TooltipProvider>
      </div>

      <div className="mt-3 w-full md:max-w-lg xl:max-w-2xl">
        <StudyInput
          onSendMessage={handleSubmit}
          onSearchStateChange={setIsSearchEnabled}
          onContentReferencesChange={setContentReferenceIds}
          placeholder={t("dashboard.learnAnything")}
          maxWidth="2xl"
          userPlan={userPlan}
          onUpgradeRequest={() => setShowPricingDialog(true)}
        />
      </div>

      <ContentUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onContentCreated={handleContentCreated}
        roomId={roomId}
      />
      
      <PasteContentDialog
        open={pasteDialogOpen}
        onOpenChange={setPasteDialogOpen}
        onContentCreated={handleContentCreated}
        roomId={roomId}
      />

      <RecordingTypeDialog
        open={recordingTypeOpen}
        onOpenChange={setRecordingTypeOpen}
        onSelect={handleRecordingSourceSelect}
        isCreating={isCreatingRecording}
      />
    </div>
  );
};

type AccentKey = "emerald" | "blue" | "rose";

const ACCENT_STYLES: Record<AccentKey, { bg: string; fg: string }> = {
  emerald: {
    bg: "bg-emerald-500/10 dark:bg-emerald-400/10",
    fg: "text-emerald-600 dark:text-emerald-400",
  },
  blue: {
    bg: "bg-sky-500/10 dark:bg-sky-400/10",
    fg: "text-sky-600 dark:text-sky-400",
  },
  rose: {
    bg: "bg-rose-500/10 dark:bg-rose-400/10",
    fg: "text-rose-600 dark:text-rose-400",
  },
};

interface ActionCardProps {
  onClick: () => void;
  loading?: boolean;
  title: string;
  description: string;
  tooltip?: string;
  accent: AccentKey;
  Icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>;
}

const ActionCard: React.FC<ActionCardProps> = ({
  onClick,
  loading,
  title,
  description,
  tooltip,
  accent,
  Icon,
}) => {
  const styles = ACCENT_STYLES[accent];
  const card = (
    <div className="w-full flex-1 sm:w-1/3">
      <div
        onClick={onClick}
        className={cn(
          "group relative cursor-pointer rounded-2xl border border-border bg-card text-card-foreground shadow-sm transition-all duration-200",
          "active:scale-[0.98] active:bg-muted/40",
          "sm:rounded-3xl sm:active:scale-100 sm:active:bg-card sm:hover:shadow-md"
        )}
      >
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-background/80 sm:rounded-3xl">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Mobile layout: horizontal tile with icon chip + chevron */}
        <div className="flex items-center gap-4 p-4 sm:hidden">
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
              styles.bg
            )}
          >
            <Icon className={cn("h-5 w-5", styles.fg)} aria-hidden />
          </div>
          <div className="flex min-w-0 flex-1 flex-col text-left">
            <h3 className="text-base font-semibold leading-tight text-foreground">
              {title}
            </h3>
            <p className="truncate text-sm text-muted-foreground">
              {description}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/50" aria-hidden />
        </div>

        {/* Desktop layout: vertical card (preserves original look) */}
        <div className="hidden h-[112px] flex-col items-start justify-center gap-y-1 px-5 py-4 sm:flex">
          <Icon
            className="mb-2 h-6 w-6 flex-shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
            aria-hidden
          />
          <div className="flex flex-col justify-center text-left">
            <h3 className="text-base font-medium text-muted-foreground transition-colors group-hover:text-foreground">
              {title}
            </h3>
            <p className="text-sm text-left text-muted-foreground/70 transition-colors group-hover:text-muted-foreground">
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (!tooltip) return card;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{card}</TooltipTrigger>
      <TooltipContent side="top" className="flex items-center gap-2 max-w-xs">
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <span>{tooltip}</span>
      </TooltipContent>
    </Tooltip>
  );
};

